import { makeAutoObservable, runInAction } from "mobx";
import { 
    AuthErrorCodes, 
    getAuth, 
    signInWithEmailAndPassword, 
    signOut, 
    onAuthStateChanged, 
    User as FirebaseUser, 
    getIdTokenResult
} from "firebase/auth";
import { auth } from "../../firestore-config";
import { router } from "../router/Routes";
import { toast } from 'react-toastify';
import { User } from "../models/user";
import { RootStore } from "./rootStore";

export default class UserStore {
    user: User | null = null;
    loading = true;    
    rootStore: RootStore;
    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this);
        
        // Combined Auth Observer: Handles persistence and cart syncing
        onAuthStateChanged(auth, (firebaseUser) => {
            if (firebaseUser) {
                const tokenResult = getIdTokenResult(firebaseUser);
            tokenResult.then((result)=> this.mapAndSetUser(firebaseUser,String(result.claims.role || 'user')));
            } else {
                runInAction(() => {
                    this.user = null;
                    this.loading = false;
                });
            }
            // Sync cart whenever auth status changes
            this.rootStore.cartStore.syncCart(firebaseUser?.uid);
        });
    }

    get isLoggedIn() {
        return !!this.user;
    }

    // Centralized mapping logic to convert Firebase User to your App User model
    private mapAndSetUser = (firebaseUser: FirebaseUser,role:string) => {        
        const newUser: User = {
            uid: firebaseUser.uid,
            name: firebaseUser.displayName || "User",
            token: (firebaseUser as any).accessToken || firebaseUser.refreshToken,
            email: firebaseUser.email!,
            authProvider: "email",
            active: false,
            photoUrl: firebaseUser.photoURL,
            actionDate: new Date(),
            actionUser: "",
            id: "",
            userType: "Visitor",
            status: "Pending",
            role:role
        };

        runInAction(() => {
            this.user = newUser;
            this.loading = false;
            this.rootStore.commonStore.setToken(newUser.token);
        });
    };

    login = async (creds: { email: string; password: any }) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, creds.email, creds.password);
            const tokenResult = await getIdTokenResult(userCredential.user);
            const role:string = String(tokenResult.claims.role) || 'user';
            this.mapAndSetUser(userCredential.user,role);
            router.navigate('/dashboard');
        } catch (err: any) {
            if (err.code === AuthErrorCodes.INVALID_PASSWORD || err.code === AuthErrorCodes.USER_DELETED) {
                toast.error('The email address or password is incorrect');
            } else {
                console.error("Login Error:", err.code);
                toast.error(err.message);
            }
            throw err;
        }
    };

    logout = async () => {
        try {
            await signOut(auth);
            runInAction(() => {
                this.user = null;
                this.rootStore.commonStore.setToken(null);
                this.rootStore.cartStore.items = []; // Clear cart from GuestStore logic
            });
            router.navigate('/');
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    // Keep for manual updates if needed
    setUser = (user: User) => {
        runInAction(() => {
            this.user = user;
            this.rootStore.commonStore.setToken(user.token);
        });
    };

    register = async (creds: User) => {
        try {
            // Implement registration logic here
            this.rootStore.modalStore.closeModal();
        } catch (error) {
            throw error;
        }
    };

    get isAdmin() {
        return this.user?.role === 'admin';
    }
    get roleLevel() {
        const levels = { 'user': 0, 'admin': 1, 'super-admin': 2 };
        return levels[this.user?.role as keyof typeof levels] || 0;
    }
}